<template>
	<div
		:style="`::-webkit-scrollbar-track { background: ${isDark ? 'var(--p-surface-900)' : '#FFFFFF'}};`"
		class="h-screen w-screen overflow-hidden"
	>
		<router-view></router-view>
		<ShortcutCheatsheetOverlay />
		<SetupWizardDialog />
	</div>
</template>
<script lang="ts">
import { computed, defineComponent, inject, onBeforeMount, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { App } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { reloadAppAfterLogout } from 'boot/axiosAuthDeniedReason';
import { simpleObservableStore } from 'boot/simpleObservableStore';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { FaviconBadge } from 'favorite-icon-badge';
import { resolveSetupWizardVisibilityAction } from 'src/composables/setupWizardVisibility';
import { useBreakpoint } from 'src/composables/useBreakpoint';
import { useDarkMode } from 'src/composables/useDarkMode';
import { useSetupWizard } from 'src/composables/useSetupWizard';
import { useAuthenticationStore } from 'src/stores/authentication';
import { useChangelogStore } from 'src/stores/changelog';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useMenuStore } from 'src/stores/menu';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import SetupWizardDialog from 'src/components/setupWizard/SetupWizardDialog.vue';
import ShortcutCheatsheetOverlay from 'src/components/shortcuts/ShortcutCheatsheetOverlay.vue';

import { UnfinishedElementCount } from 'src/models/menu';

import { AppService } from 'src/services/app/app.service';
import { CapacitorAppService } from 'src/services/app/capacitorApp.service';

import { syncPreferredTextZoom } from 'src/util/capacitor/textZoom';
import { isChangelogEnabled } from 'src/util/changelog/changelogVisibility';
import { CommandBus } from 'src/util/command/CommandBus';
import { EventBus } from 'src/util/eventBus/EventBus';
import { isAndroid, isIos } from 'src/util/platform';
import {
	canAutoOpenSetupWizard,
	isRunningInNativeCapacitorApp,
} from 'src/util/platform/browserInteractionCapabilities';
import { QueryStore } from 'src/util/query/QueryStore';
import { applyDarkMode } from 'src/util/theme/applyDarkMode';

const NATIVE_PLATFORM_BODY_CLASSES = ['native-mobile', 'platform-android', 'platform-ios'] as const;
const RESUME_VIEWPORT_RESIZE_DELAYS_MS = [0, 50, 250] as const;

export default defineComponent({
	components: {
		SetupWizardDialog,
		ShortcutCheatsheetOverlay,
	},
	name: 'App',
	setup() {
		const { isDark } = useDarkMode();
		const setupWizard = useSetupWizard();
		const { width } = useBreakpoint();
		const setupWizardAutoOpenSupported = computed<boolean>(() => canAutoOpenSetupWizard(width.value));
		const { t } = useI18n();
		const commandBus = inject<CommandBus | undefined>('commandBus', undefined);
		const queryStore = inject<QueryStore | undefined>('queryStore', undefined);
		const eventBus = inject('eventBus') as EventBus;
		const router = useRouter();
		const route = useRoute();
		const runtimeStore = {} as RuntimeStoreContext;
		const genericActivityListStore = useGenericActivityListStore();
		const authenticationStore = useAuthenticationStore();
		const changelogStore = useChangelogStore();
		const menuStore = useMenuStore();
		const userInterfaceSettingsStore = useUserInterfaceSettingsStore();
		const applyPreferredTextZoom = (): void => {
			if (isRunningInNativeCapacitorApp()) {
				void syncPreferredTextZoom();
			}
		};

		let appService: AppService | null = null;
		let appStateChangeListener: Promise<{ remove: () => Promise<void> }> | null = null;
		let safeAreaChangedListener: Promise<{ remove: () => Promise<void> }> | null = null;
		let keyboardDidShowListener: Promise<{ remove: () => Promise<void> }> | null = null;
		let keyboardDidHideListener: Promise<{ remove: () => Promise<void> }> | null = null;

		const clearNativePlatformBodyClasses = (): void => {
			document.body.classList.remove(...NATIVE_PLATFORM_BODY_CLASSES);
		};

		const syncNativePlatformBodyClasses = (): void => {
			clearNativePlatformBodyClasses();

			if (!isRunningInNativeCapacitorApp()) {
				return;
			}

			document.body.classList.add('native-mobile');

			if (isIos()) {
				document.body.classList.add('platform-ios');
			} else if (isAndroid()) {
				document.body.classList.add('platform-android');
			}
		};

		const applySafeAreaInsets = (insets: object): void => {
			for (const [key, value] of Object.entries(insets)) {
				document.documentElement.style.setProperty(`--safe-area-inset-${key}`, `${String(value)}px`);
			}
		};

		const syncNativeSafeAreaInsets = (): void => {
			void SafeArea.getSafeAreaInsets().then(({ insets }) => {
				applySafeAreaInsets(insets);
			});
		};

		const recoverNativeViewportAfterResume = (): void => {
			if (!isRunningInNativeCapacitorApp()) {
				return;
			}

			void Keyboard.hide().catch(() => undefined);
			syncNativeSafeAreaInsets();

			RESUME_VIEWPORT_RESIZE_DELAYS_MS.forEach((delayMs) => {
				window.setTimeout(() => {
					window.dispatchEvent(new Event('resize'));
				}, delayMs);
			});
		};

		const badge = new FaviconBadge({
			backgroundColor: '#ff0000',
			faviconSrc: '/icons/icon-128x128.png',
			fontFamily: 'arial',
			fontStyle: 'bold',
			maxCount: 99,
			positionX: 'right',
			positionY: 'bottom',
			size: 100,
			strokeColor: '#000',
			textColor: '#fff',
		});

		if (simpleObservableStore.showVersionMismatch) {
			void router.push({
				name: 'versionMismatch',
			});
		} else {
			const splitViewEnabled = computed((): boolean => userInterfaceSettingsStore.splitViewEnabled);

			if (isRunningInNativeCapacitorApp() && queryStore !== undefined && commandBus !== undefined) {
				void genericActivityListStore.setActiveModule(route.name as string);

				appService = new CapacitorAppService(
					t,
					runtimeStore,
					router,
					route,
					queryStore,
					commandBus,
					eventBus,
					window,
					splitViewEnabled.value
				);
			} else if (queryStore !== undefined && commandBus !== undefined) {
				void genericActivityListStore.setActiveModule(route.name as string);

				appService = new AppService(
					t,
					runtimeStore,
					router,
					route,
					queryStore,
					commandBus,
					eventBus,
					window,
					splitViewEnabled.value
				);
			}

			const handleAppStateChange = ({ isActive }: { isActive: boolean }): void => {
				if (isActive) {
					applyPreferredTextZoom();
					applyDarkMode(userInterfaceSettingsStore.themeMode);
					recoverNativeViewportAfterResume();
				}

				if (appService !== null) {
					if (isActive) {
						appService.appHasMovedToTheForeground();
					} else {
						appService.appHasMovedToTheBackground();
					}
				}
			};

			appStateChangeListener = Promise.resolve(App.addListener('appStateChange', handleAppStateChange));

			watch(
				() => route.name,
				(routeName) => {
					void genericActivityListStore.setActiveModule(routeName as string);
				}
			);

			watch(
				() => menuStore.unfinishedElementCountList,
				(unfinishedElementCountList: Array<UnfinishedElementCount>) => {
					let badgeCount = 0;
					unfinishedElementCountList.forEach((unfinishedElementCountListItem) => {
						badgeCount += unfinishedElementCountListItem.count;
					});

					badge.set(badgeCount);
				}
			);

			watch(
				() => authenticationStore.loggedIn,
				(loggedIn) => {
					if (!loggedIn) {
						reloadAppAfterLogout(window.location, simpleObservableStore.authDeniedReason);
					}
				}
			);

			watch(
				() =>
					[
						authenticationStore.loggedIn,
						userInterfaceSettingsStore.userPreferencesLoaded,
						route.name,
						setupWizardAutoOpenSupported.value,
						setupWizard.isDeferred.value,
					] as const,
				([loggedIn, preferencesLoaded, routeName, deviceSupportsAutoOpen, deferredInSession]) => {
					const action = resolveSetupWizardVisibilityAction({
						deferredInSession,
						deviceSupportsAutoOpen,
						loggedIn,
						preferencesLoaded,
						routeName,
					});

					if (action === 'close') {
						setupWizard.close();
						return;
					}

					if (action === 'openIfNeeded') {
						setupWizard.openIfNeeded();
					}
				},
				{ immediate: true }
			);

			watch(
				() => authenticationStore.loggedIn,
				(loggedIn) => {
					if (loggedIn && isChangelogEnabled()) {
						void changelogStore.fetchChangelog();
					}
				},
				{ immediate: true }
			);

			watch(
				() => simpleObservableStore.showVersionMismatch,
				(showVersionMismatch) => {
					if (showVersionMismatch) {
						void router.push({
							name: 'versionMismatch',
						});
					}
				}
			);
		}

		onBeforeMount(() => {
			if (appService !== null) {
				appService.initializeTheApp();
			}
		});

		onMounted(() => {
			syncNativePlatformBodyClasses();
			applyPreferredTextZoom();

			if (isRunningInNativeCapacitorApp()) {
				void SafeArea.removeAllListeners();
				syncNativeSafeAreaInsets();

				safeAreaChangedListener = Promise.resolve(
					SafeArea.addListener('safeAreaChanged', ({ insets }) => {
						applySafeAreaInsets(insets);
					})
				);

				keyboardDidShowListener = Promise.resolve(
					Keyboard.addListener('keyboardDidShow', () => {
						if (isAndroid()) {
							document.documentElement.style.setProperty('--safe-area-inset-bottom', '0px');
						}
					})
				);

				keyboardDidHideListener = Promise.resolve(
					Keyboard.addListener('keyboardDidHide', () => {
						if (isAndroid()) {
							syncNativeSafeAreaInsets();
						}
					})
				);
			}

			if (appService !== null) {
				appService.appHasMovedToTheForeground();
				if (isRunningInNativeCapacitorApp() && queryStore !== undefined && commandBus !== undefined) {
					(appService as CapacitorAppService).capacitorAppHasMovedToTheForeground();
				}
			}
		});

		onUnmounted(() => {
			clearNativePlatformBodyClasses();

			if (appService !== null) {
				appService.appHasMovedToTheBackground();
				appService.cleanup();
			}

			if (appStateChangeListener !== null) {
				void appStateChangeListener.then((listener) => listener.remove());
				appStateChangeListener = null;
			}

			if (safeAreaChangedListener !== null) {
				void safeAreaChangedListener.then((listener) => listener.remove());
				safeAreaChangedListener = null;
			}

			if (keyboardDidShowListener !== null) {
				void keyboardDidShowListener.then((listener) => listener.remove());
				keyboardDidShowListener = null;
			}

			if (keyboardDidHideListener !== null) {
				void keyboardDidHideListener.then((listener) => listener.remove());
				keyboardDidHideListener = null;
			}
		});

		return {
			isDark,
			simpleObservableStore,
		};
	},
});
</script>

<style lang="scss"></style>
